import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { db, type UserSettings } from './db';
import { applyReminderSchedule } from './nativeNotifications';

interface SettingsContextValue {
  settings: UserSettings | null;
  loading: boolean;
  refresh: () => Promise<void>;
  save: (next: UserSettings) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

async function syncSchedule(settings: UserSettings | null): Promise<void> {
  if (!settings) return;
  await applyReminderSchedule({
    reminderTimes: settings.reminderTimes,
    notificationsEnabled: settings.notificationsEnabled,
  });
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const rows = await db.userSettings.toArray();
    setSettings(rows[0] ?? null);
  };

  const save = async (next: UserSettings) => {
    // Look up the existing row's id before `put` so we update in place
    // rather than auto-incrementing a second orphaned row.
    let toWrite = next;
    if (toWrite.id == null) {
      const existing = await db.userSettings.toArray();
      const existingId = existing[0]?.id;
      if (existingId != null) {
        toWrite = { ...next, id: existingId };
      }
    }
    await db.userSettings.put(toWrite);
    await refresh();
    try {
      await syncSchedule(toWrite);
    } catch (error) {
      console.error('Failed to sync native notifications:', error);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const rows = await db.userSettings.toArray();
        setSettings(rows[0] ?? null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Re-sync the OS schedule whenever settings finish loading or change.
  // Cheap on web (no-op) and idempotent on native.
  useEffect(() => {
    if (loading) return;
    void syncSchedule(settings).catch((error: unknown) => {
      console.error('Failed to sync native notifications on load:', error);
    });
  }, [loading, settings]);

  const value: SettingsContextValue = { settings, loading, refresh, save };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook for the provider in this file
export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useSettings must be used inside <SettingsProvider>');
  }
  return ctx;
}
